import React, { useState, useRef, useMemo } from "react";
import { Drawer, Badge, Button } from "antd";
import dayjs from "dayjs";
import ArrowArcRight from "Assets/Images/filter-icon.svg";
import FiltersFormScreen, {
  FiltersFormScreenRef,
} from "Pages/MyScheduleBeta/Components/FiltersSchedule/FiltersFormScreen";
import { useScheduleBetaContext } from "Pages/MyScheduleBeta/Context/ScheduleBetaContext";
import { DAY_WEEK_TYPE } from "Pages/MyScheduleBeta/utils/constants";
import { toISTStart, toISTEnd } from "Pages/MyScheduleBeta/utils/dateHelpers";
import DraggableModal from "utilFunctions/shared/DraggableModal";
import useGlobalSearchContext from "Context/GlobalSearchContext";
import useResponsive from "Context/useResponsive";

const MyScheduleBetaFilter: React.FC = () => {
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const { isDesktop } = useResponsive();
  const {
    filterState,
    eventType,
    setFilterState,
    setSelectedDate,
    setSearchedFaculty,
  } = useScheduleBetaContext();
  const { globalSearchData, saveGlobalSearchData } = useGlobalSearchContext();
  const filterFormRef = useRef<FiltersFormScreenRef>(null);
  const showDrawer = () => setIsDrawerVisible(true);
  const closeDrawer = () => setIsDrawerVisible(false);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filterState?.subject?.value && filterState.subject.value !== "ALL") {
      count += 1;
    }
    if (filterState?.facultySearch?.employeeId) {
      count += 1;
    }
    if (!filterState?.startDate || !filterState?.endDate) {
      return count;
    }
    const today = dayjs();
    if (eventType === DAY_WEEK_TYPE.DAY) {
      const defaultStart = toISTStart(today.toDate());
      const defaultEnd = toISTEnd(today.toDate());
      if (
        filterState.startDate !== defaultStart ||
        filterState.endDate !== defaultEnd
      ) {
        count += 1;
      }
    } else {
      const defaultStart = toISTStart(today.subtract(3, "day").toDate());
      const defaultEnd = toISTEnd(today.add(3, "day").toDate());
      if (
        filterState.startDate !== defaultStart ||
        filterState.endDate !== defaultEnd
      ) {
        count += 1;
      }
    }
    return count;
  }, [filterState, eventType]);

  React.useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("scheduleFilterCountChanged", {
        detail: {
          activeFilterCount,
        },
      })
    );
  }, [activeFilterCount]);

  const handleClearAll = () => {
    const today = dayjs();
    setSelectedDate(today.format("YYYY-MM-DD"));
    if (eventType === DAY_WEEK_TYPE.DAY) {
      setFilterState((prev: any) => ({
        ...prev,
        startDate: toISTStart(today.toDate()),
        endDate: toISTEnd(today.toDate()),
        subject: null,
        facultySearch: "",
        clearFacultySearchInput: true,
      }));
    } else {
      const start = today.subtract(3, "day");
      const end = today.add(3, "day");
      setFilterState((prev: any) => ({
        ...prev,
        startDate: toISTStart(start.toDate()),
        endDate: toISTEnd(end.toDate()),
        subject: null,
        facultySearch: "",
        clearFacultySearchInput: true,
      }));
    }
    setSearchedFaculty(null);
    localStorage.removeItem("scheduledFilter");
  };

  const onMbClose = () => {
    saveGlobalSearchData({
      pathurl: "/admin/schedule-beta",
      isStudentHealth: false,
      isBranchHealth: false,
      isPerformance: false,
      isSyllabusCompliances: false,
      isBatches: false,
      isMyBatch: false,
      isScheduleBeta: false,
    });
  };

  return (
    <>
      {isDesktop ? (
        <Drawer
          title={null}
          closeIcon={null}
          placement="right"
          onClose={closeDrawer}
          open={isDrawerVisible}
          width={400}
        >
          <FiltersFormScreen
            ref={filterFormRef}
            onClose={closeDrawer}
            isOpen={isDrawerVisible}
          />
        </Drawer>
      ) : (
        <DraggableModal
          isPaneOpen={globalSearchData?.isScheduleBeta}
          togglePane={onMbClose}
          onClose={onMbClose}
          initHeight={600}
        >
          <div className="pl-3 pt-0 pr-3" style={{ height: "100%" }}>
            <FiltersFormScreen
              ref={filterFormRef}
              onClose={onMbClose}
              isOpen={!!globalSearchData?.isScheduleBeta}
            />
          </div>
        </DraggableModal>
      )}
      {isDesktop && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          {activeFilterCount > 0 && (
            <span
              onClick={handleClearAll}
              style={{
                color: "#ff4d4f",
                cursor: "pointer",
                fontWeight: 500,
                fontSize: "14px",
                userSelect: "none",
                whiteSpace: "nowrap",
              }}
            >
              Clear All
            </span>
          )}

          <Badge count={activeFilterCount} offset={[-2, 2]}>
            <Button
              onClick={showDrawer}
              icon={
                <img
                  src={ArrowArcRight}
                  alt="Filter"
                  style={{
                    width: "16px",
                    height: "16px",
                  }}
                />
              }
            />
          </Badge>
        </div>
      )}
    </>
  );
};

export default MyScheduleBetaFilter;
